/**
 * GAF Animations v1.0.1
 * Scroll-triggered reveal system for GAF sections.
 * Uses IntersectionObserver — no external libraries.
 * Respects prefers-reduced-motion.
 */

(function () {
  'use strict';

  // Bail out if user prefers reduced motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // ── Configuration ────────────────────────────────────
  const THRESHOLD      = 0.18;   // % of element visible before triggering
  const ROOT_MARGIN    = '0px 0px -40px 0px'; // trigger 40px before bottom of viewport
  const STAGGER_MS     = 110;    // delay between staggered children (ms)

  // ── Elements to auto-animate (selector → animation type) ──
  const AUTO_TARGETS = [
    // Brand story — sides slide in
    { sel: '.gaf-brand-story__text-col',    anim: 'fade-right', stagger: false },
    { sel: '.gaf-brand-story__image-col',   anim: 'fade-left',  stagger: false },

    // Atelier — image + content
    { sel: '.gaf-atelier__image-col',       anim: 'fade-left',  stagger: false },
    { sel: '.gaf-atelier__text-col',         anim: 'fade-right', stagger: false },

    // Quote — scale + fade
    { sel: '.gaf-quote__mark',             anim: 'fade-in',    stagger: false },
    { sel: '.gaf-quote__text',             anim: 'scale',      stagger: false },
    { sel: '.gaf-quote__attribution',      anim: 'fade-up',    stagger: false },

    // Invitation form — sides
    { sel: '.gaf-invite__form-wrap',        anim: 'fade-left',  stagger: false },
    { sel: '.gaf-invite__inner',            anim: 'fade-up',    stagger: false },

    // Footer columns — stagger up
    { sel: '.gaf-footer__brand-col',        anim: 'fade-up',    stagger: false },
    { sel: '.gaf-footer__nav-col',          anim: 'fade-up',    stagger: true  },
    { sel: '.gaf-footer__icons-col',        anim: 'fade-up',    stagger: false },

    // Generic fallback for section headings / bodies in GAF sections
    // Atelier children animated via parent text-col

    { sel: '.gaf-brand-story__heading',     anim: 'fade-up',    stagger: false },
    { sel: '.gaf-brand-story__body',        anim: 'fade-up',    stagger: false },

    // Story visionary — image slides from inline-start edge, text from inline-end edge
    { sel: '.gaf-story-visionary__image-col', anim: 'fade-left',  stagger: false },
    { sel: '.gaf-story-visionary__text-col',  anim: 'fade-right', stagger: false },

    // Story atelier — header drops in, photo + card slide from edges
    { sel: '.gaf-story-atelier__header',      anim: 'fade-up',    stagger: false },
    { sel: '.gaf-story-atelier__photo-col',   anim: 'fade-left',  stagger: false },
    { sel: '.gaf-story-atelier__card',        anim: 'fade-right', stagger: false },

    // Story cultural — stat tile scales in dramatically, text tile slides from inline-end
    { sel: '.gaf-story-cultural__stat-tile',  anim: 'scale',      stagger: false },
    { sel: '.gaf-story-cultural__text-tile',  anim: 'fade-right', stagger: false },

    // Our Stores page — header then cards stagger up
    { sel: '.gaf-stores__hero-inner',         anim: 'fade-up',    stagger: false },
    { sel: '.gaf-stores__card',               anim: 'fade-up',    stagger: true  },

    // Dawn sections that benefit from scroll reveal
    { sel: '.featured-collection .collection-list__item',   anim: 'fade-up', stagger: true  },
    { sel: '.multicolumn-list__item',                        anim: 'fade-up', stagger: true  },
    { sel: '.related-products .product-recommendations__list > *', anim: 'fade-up', stagger: true },
  ];

  // RTL-aware: flip left/right animations
  function resolveAnim(anim) {
    const rtl = document.documentElement.dir === 'rtl';
    if (!rtl) return anim;
    if (anim === 'fade-left')  return 'fade-right';
    if (anim === 'fade-right') return 'fade-left';
    return anim;
  }

  // ── Mark an element for reveal ───────────────────────
  function prepareElement(el, anim, delayMs) {
    if (el.classList.contains('gaf-reveal')) return; // already prepared
    el.classList.add('gaf-reveal');
    el.setAttribute('data-anim', resolveAnim(anim));
    if (delayMs) el.style.setProperty('--reveal-delay', `${delayMs}ms`);
  }

  // ── Intersection Observer callback ───────────────────
  function onIntersect(entries, observer) {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target); // only animate once
    });
  }

  const observer = new IntersectionObserver(onIntersect, {
    rootMargin: ROOT_MARGIN,
    threshold:  THRESHOLD,
  });

  // ── Initialise all auto targets ───────────────────────
  function init() {
    let staggerGroups = {};

    AUTO_TARGETS.forEach(({ sel, anim, stagger }) => {
      const elements = document.querySelectorAll(sel);
      if (!elements.length) return;

      // Group staggered siblings by parent
      if (stagger) {
        elements.forEach(el => {
          const parentKey = el.parentElement
            ? el.parentElement.dataset.gafStagger || (el.parentElement.dataset.gafStagger = Math.random().toString(36).slice(2))
            : 'root';
          if (!staggerGroups[parentKey]) staggerGroups[parentKey] = [];
          staggerGroups[parentKey].push({ el, anim });
        });
      } else {
        elements.forEach(el => {
          prepareElement(el, anim, 0);
          observer.observe(el);
        });
      }
    });

    // Apply stagger delays
    Object.values(staggerGroups).forEach(group => {
      group.forEach(({ el, anim }, i) => {
        prepareElement(el, anim, i * STAGGER_MS);
        observer.observe(el);
      });
    });
  }

  // ── Quote section: also wrap inner content ────────────
  function initQuoteSection() {
    document.querySelectorAll('.gaf-quote__content').forEach(el => {
      // The whole quote block gets a subtle scale
      if (!el.classList.contains('gaf-reveal')) {
        el.classList.add('gaf-reveal');
        el.setAttribute('data-anim', 'scale');
        observer.observe(el);
      }
    });
  }

  // ── On page ready ─────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); initQuoteSection(); });
  } else {
    init();
    initQuoteSection();
  }

  // ── Shopify Theme Editor: re-init on section reload ───
  document.addEventListener('shopify:section:load', () => {
    init();
    initQuoteSection();
  });

}());
